import { memo, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Badge, JsonContent } from '@/components/ui';
import type { PlatformEvent } from '../../types/events';
import { getEventBadgeVariant, renderEventData } from './event-renderers';

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    fractionalSecondDigits: 3,
  });
}

function EventCardInner({ event }: { event: PlatformEvent }) {
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(JSON.stringify(event, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div data-testid="event-card" className="rounded-lg border border-l-2 border-l-status-neutral-border/40 bg-card p-3 space-y-2">
      {/* Generic info row */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">
          {formatTimestamp(event.metadata.timestamp)}
        </span>
        <Badge variant="info">{event.source}</Badge>
        <Badge variant={getEventBadgeVariant(event.eventType)}>{event.eventType}</Badge>
        {event.subjectType && (
          <span className="text-xs text-muted-foreground">
            {event.subjectType} / {event.subjectId}
          </span>
        )}
        {event.eventType.startsWith('agent.run.') && typeof event.data?.agentName === 'string' && (
          <span className="text-xs text-muted-foreground">
            agent: <span className="text-foreground">{event.data.agentName}</span>
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={handleCopy}
            title="Copy event JSON"
            className="p-1 rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
          <button
            type="button"
            onClick={() => setShowJson((v) => !v)}
            className="text-xs px-2 py-0.5 rounded border bg-muted hover:bg-accent transition-colors"
          >
            JSON
          </button>
        </div>
      </div>

      {/* Event-specific section */}
      {renderEventData(event.eventType, event.data)}

      {/* Raw JSON */}
      {showJson && (
        <JsonContent maxHeight="24rem">{JSON.stringify(event)}</JsonContent>
      )}
    </div>
  );
}

const EventCard = memo(EventCardInner);
export default EventCard;
